import {
  Form,
  Forms,
  RadioButton,
  RadioGroup,
  Select,
  useFormsContext,
} from '@ally/forms'
import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  ButtonGroup,
  Label,
  TextBody,
  upToMd,
  useModalContext,
  Link as MuiLink,
  Accordion,
  Alert,
} from '@ally/metronome-ui'
import { Private } from '@ally/private'
import allytm from '@ally/allytm'
import { Beneficiary } from '../../../types'
import { AddBeneficiary } from './AddBeneficiaryModal'
import { OwnershipTypeField } from '../../NonIraBeneficiaries/AccountOwnership/OwnershipTypeField'
import { useIraBeneficiaries } from '../../../services/IraBeneficiariesProvider'
import { useFeatureFlags } from '../../../hooks/use-feature-flags'
import { Flags } from '../../../utils/feature-flags'
import { KeyValuePairObj, fireErrorTagging } from '../../../utils/tagging'

export interface FormValues {
  addType: string
  beneficiarySelection: string
  ownershipType?: string
  isMarried: string
}

export const AddBeneTypeSelectionInnerForm: React.FC<{
  isLoading: boolean
  listOfBene: Beneficiary[]
  hasExistingBenes: boolean
  isIraPage: boolean
  setOwnershipTypeSelection: React.Dispatch<React.SetStateAction<string>>
  hasPrimaryBene?: number
  hasSpouseBene: boolean
}> = ({
  isLoading,
  listOfBene,
  hasExistingBenes,
  isIraPage,
  setOwnershipTypeSelection,
  hasPrimaryBene,
  hasSpouseBene,
}) => {
  const { formId, handleSubmit, values, setFieldValue, errors } =
    useFormsContext<Partial<FormValues>>()
  const { setOpen } = useModalContext()
  const { isCommunityPropertyState } = useIraBeneficiaries()
  const [formError, setFormaError] = useState(false)

  const ffBeneficiariesCommunityPropertyFix = useFeatureFlags(
    Flags.BeneficiariesCommunityPropertyFix,
  )

  const options = listOfBene.map(bene => {
    return {
      label: `${bene.firstName || bene.beneficiaryName} ${
        bene.middleName || ''
      } ${bene.lastName || ''}`,
      value: bene.beneficiaryId,
    }
  })

  const validateSelection = (val: string): string => {
    if (!val && values.addType === 'from-existing') {
      return 'Make a selection.'
    }
    return ''
  }

  useEffect(() => {
    fireErrorTagging(errors as KeyValuePairObj)
  }, [errors])

  const handleChange = (value: string): void => {
    setFieldValue('beneficiarySelection', '')
    setFieldValue('addType', value)
  }

  const validateIsMarried = (val: string): string => {
    if (!val) {
      return 'Make a selection.'
    }
    // istanbul ignore next // Low value test
    return ''
  }

  // istanbul ignore next // POC - Will add if we final this flow
  const handleClick = (): void => {
    if (
      values.isMarried === 'Yes' &&
      hasPrimaryBene &&
      ffBeneficiariesCommunityPropertyFix
    ) {
      setFormaError(true)
      allytm.event(
        'customError',
        'If your marital status changes, you may need to update your beneficiary information. First, delete your current primary beneficiary, then add the new primary beneficiary.',
      )
    } else {
      handleSubmit()
    }
  }

  // istanbul ignore next // POC - Will add if we final this flow
  const handleMarriedChange = (value: string): void => {
    setFieldValue('isMarried', value)
    if (value === 'No') {
      setFormaError(false)
    }
  }

  const showAddBeneSelectionOptions = isCommunityPropertyState
    ? values.isMarried && listOfBene.length > 0
    : listOfBene.length > 0

  const handleOwnershipTypeChangeFromExisting = (
    value: string | undefined,
  ): void => {
    if (value) {
      setOwnershipTypeSelection(value)
    }
  }

  const showAddMySpouseOption = ffBeneficiariesCommunityPropertyFix
    ? // istanbul ignore next - POC - Will add if we final this flow
      values.isMarried === 'Yes' && !hasSpouseBene
    : values.isMarried === 'Yes' && !hasPrimaryBene

  const showAddBeneficiaryHeading =
    (values.isMarried === 'No' && listOfBene.length > 0) ||
    (values.isMarried === 'Yes' && !showAddMySpouseOption)

  return (
    <Form id={formId}>
      {formError && (
        // istanbul ignore next - POC - Will add if we final this flow
        <Box mb="md">
          <Alert
            variant="error"
            disableContainer
            body="If your marital status changes, you may need to update your beneficiary information. First, delete your current primary beneficiary, then add the new primary beneficiary."
            focusOnMount
          />
        </Box>
      )}
      {isCommunityPropertyState && (
        <Box>
          <Label htmlFor="isMarried">Are you married?</Label>
          <RadioGroup
            name="isMarried"
            legendText=""
            isLegendHidden
            disableDefaultSelection
            validate={validateIsMarried}
            onChange={handleMarriedChange}
          >
            <RadioButton label="Yes" value="Yes" data-testid="is-married-yes" />
            <RadioButton label="No" value="No" data-testid="is-married-no" />
          </RadioGroup>
          {showAddBeneficiaryHeading && (
            <Box mb="sm">
              <TextBody size="sm" tag="p" weight="bold">
                Choose or add a beneficiary.
              </TextBody>
            </Box>
          )}
          {showAddMySpouseOption && (
            <Box>
              <Box mb="sm">
                <TextBody size="sm" tag="p" weight="bold">
                  Choose or add your spouse.
                </TextBody>
              </Box>
              <TextBody size="sm" tag="p">
                Your state requires your spouse to be a beneficiary by default,
                but you can complete the{' '}
                <MuiLink
                  size="md"
                  href="https://www.ally.com/content/dam/pdf/bank/change-beneficiary.pdf"
                  text="Change of Beneficiary form"
                  target="_blank"
                  allytmln="changeBeneficiaryForm"
                />{' '}
                (PDF) to change your primary beneficiary. This form will require
                your spouse&apos;s signature.
              </TextBody>
              <Accordion
                items={[
                  {
                    heading: 'What if my marital status changes?',
                    panel: (
                      <>
                        <TextBody size="sm" tag="p" weight="bold" tabIndex={-1}>
                          Change in Marital Status?
                        </TextBody>
                        <TextBody size="sm" tag="p">
                          If your marital status changes, you may need to update
                          your beneficiary information. First, delete your
                          current primary beneficiary, then add the new primary
                          beneficiary.
                        </TextBody>
                      </>
                    ),
                  },
                ]}
                disableContainer
              />
            </Box>
          )}
        </Box>
      )}
      {showAddBeneSelectionOptions && (
        <RadioGroup
          name="addType"
          legendText=""
          isLegendHidden
          defaultValue="from-existing"
          onChange={handleChange}
        >
          <RadioButton
            label="Choose from existing"
            value="from-existing"
            data-testid="choose-from-existing"
          />

          <Box ml="40px" mb="lg">
            {values.addType === 'from-existing' && (
              <Box width={upToMd('88%', '50%')}>
                <Private>
                  <Select
                    name="beneficiarySelection"
                    data-testid="distribute-to-dropdown"
                    options={options}
                    placeholder="Selection"
                    label="Beneficiary"
                    validate={validateSelection}
                  />
                </Private>
              </Box>
            )}
            {values.beneficiarySelection && !hasExistingBenes && !isIraPage && (
              <Box width={upToMd('88%', '50%')} mt="md">
                <OwnershipTypeField
                  handleOwnershipTypeChangeFromExisting={
                    handleOwnershipTypeChangeFromExisting
                  }
                />
              </Box>
            )}
          </Box>

          <RadioButton
            label={
              showAddMySpouseOption ? 'Add my spouse' : 'Add a new beneficiary'
            }
            value="add-new"
            data-testid="add-new-bene"
          />
        </RadioGroup>
      )}

      <Box mt="md">
        <ButtonGroup>
          <Button
            allytmln="continue from type selection"
            text="Continue"
            disabled={isLoading}
            onClick={handleClick}
          />
          <Button
            allytmln="Cancel Add Beneficiary"
            text="Cancel"
            onClick={(): void => setOpen(false)}
            variant="link"
          />
        </ButtonGroup>
      </Box>
    </Form>
  )
}
export const AddBeneTypeSelection: React.FC<{
  setAddBeneficiary: React.Dispatch<React.SetStateAction<AddBeneficiary>>
  listOfBene: Beneficiary[]
  hasExistingBenes: boolean
  isIraPage: boolean
  setOwnershipTypeSelection: React.Dispatch<React.SetStateAction<string>>
  existingBenes?: Beneficiary[]
}> = ({
  setAddBeneficiary,
  listOfBene,
  hasExistingBenes,
  isIraPage,
  setOwnershipTypeSelection,
  existingBenes,
}) => {
  const hasPrimaryBene = existingBenes?.filter(
    bene => bene.beneficiaryType === 'Primary',
  ).length

  const hasSpouseBene =
    existingBenes?.some(bene => bene.relationshipToOwner === 'Spouse') || false

  const [isLoading, setIsLoading] = useState(false)
  const handleSubmit = async (values: FormValues): Promise<void> => {
    setIsLoading(true)
    if (isIraPage && !values.addType && values.isMarried) {
      setAddBeneficiary({
        addType: 'add-new',
        isMarried: values.isMarried,
      })
      // istanbul ignore next - Low value test
      document.getElementById('add-bene-heading')?.focus()
    } else {
      const selectedBene = listOfBene.find(bene => {
        return bene.beneficiaryId === values.beneficiarySelection
      })
      setAddBeneficiary({
        addType: values.addType,
        beneficiarySelection: selectedBene,
        isMarried: values.isMarried,
      })
      document.getElementById('add-bene-heading')?.focus()
    }
    // istanbul ignore next
    if (isLoading) {
      setIsLoading(false)
    }
  }
  return (
    <Forms
      formId="add-beneficiaries"
      initialValues={{
        addType: '',
        beneficiarySelection: '',
        ownershipType: '',
        isMarried: '',
      }}
      onSubmit={handleSubmit}
    >
      <AddBeneTypeSelectionInnerForm
        isLoading={isLoading}
        listOfBene={listOfBene}
        hasExistingBenes={hasExistingBenes}
        isIraPage={isIraPage}
        setOwnershipTypeSelection={setOwnershipTypeSelection}
        hasPrimaryBene={hasPrimaryBene}
        hasSpouseBene={hasSpouseBene}
      />
    </Forms>
  )
}
